<template>
  <section class="training-plan">
    <div class="section-header">
      <div class="section-heading">
        <span class="section-icon"><v-icon>mdi-calendar-star-outline</v-icon></span>
        <div>
          <div class="section-eyebrow">Weiterbildung planen</div>
          <h2>Schulungsplan</h2>
          <p>Entdecke kommende Schulungen, prüfe freie Plätze und verwalte deine Anmeldung.</p>
        </div>
      </div>

      <div class="view-switch" role="group" aria-label="Darstellung wählen">
        <button type="button" :class="{ active: viewMode === 'list' }" @click="viewMode = 'list'">
          <v-icon>mdi-view-grid-outline</v-icon>
          Liste
        </button>
        <button type="button" :class="{ active: viewMode === 'calendar' }" @click="viewMode = 'calendar'">
          <v-icon>mdi-calendar-month-outline</v-icon>
          Kalender
        </button>
      </div>
    </div>

    <div v-if="loading" class="session-grid" aria-label="Schulungen werden geladen">
      <v-skeleton-loader v-for="index in 3" :key="index" dark type="image, article" class="session-skeleton" />
    </div>

    <div v-else-if="!sessions.length" class="empty-surface">
      <span class="empty-icon"><v-icon>mdi-calendar-blank-outline</v-icon></span>
      <h3>Aktuell sind keine Schulungen ausgeschrieben</h3>
      <p>Neue Termine erscheinen automatisch an dieser Stelle.</p>
    </div>

    <div v-else-if="viewMode === 'list'" class="session-grid">
      <v-card
        v-for="session in sessions"
        :key="session._id"
        dark
        flat
        class="session-card"
        role="button"
        tabindex="0"
        @click="openSession(session)"
        @keydown.enter="openSession(session)"
        @keydown.space.prevent="openSession(session)">
        <div class="card-top">
          <span class="date-tile">
            <strong>{{ formatDay(session.startsAt) }}</strong>
            <small>{{ formatMonthShort(session.startsAt) }}</small>
          </span>
          <span class="phase-pill" :class="phaseClass(getPhase(session))">{{ getPhase(session) }}</span>
        </div>

        <div class="session-copy">
          <h3>{{ session.title }}</h3>
          <p>{{ session.description || "Alle Informationen zu dieser Schulung findest du in der Detailansicht." }}</p>
        </div>

        <div class="session-facts">
          <span>
            <v-icon>mdi-clock-outline</v-icon>
            {{ formatTime(session.startsAt) }} – {{ formatTime(session.endsAt) }}
          </span>
          <span>
            <v-icon>mdi-map-marker-outline</v-icon>
            {{ session.location || "Ort noch offen" }}
          </span>
        </div>

        <div class="session-footer">
          <span class="seats">
            <v-icon>mdi-account-multiple-outline</v-icon>
            <strong>{{ session.registeredCount || 0 }}</strong> / {{ session.maxParticipants }} Plätze
          </span>
          <span
            v-if="session.myRegistration"
            class="registration-pill"
            :class="session.myRegistration.status === 'waiting_list' ? 'is-waiting' : 'is-confirmed'">
            <v-icon>{{ session.myRegistration.status === "waiting_list" ? "mdi-clock-outline" : "mdi-check" }}</v-icon>
            {{ session.myRegistration.status === "waiting_list" ? "Warteliste" : "Angemeldet" }}
          </span>
          <v-icon v-else class="card-arrow">mdi-arrow-right</v-icon>
        </div>
      </v-card>
    </div>

    <v-card v-else dark flat class="calendar-surface">
      <div class="calendar-toolbar">
        <div class="month-controls">
          <v-btn icon class="icon-button" aria-label="Vorheriger Monat" @click="previousMonth">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <h3>{{ monthLabel }}</h3>
          <v-btn icon class="icon-button" aria-label="Nächster Monat" @click="nextMonth">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
        <v-btn outlined color="#58d388" class="today-action" @click="goToToday">Heute</v-btn>
      </div>

      <div class="calendar-scroll" tabindex="0" aria-label="Schulungskalender, auf kleinen Bildschirmen horizontal scrollbar">
        <div class="calendar-grid">
          <div v-for="dayName in dayNames" :key="dayName" class="calendar-day-name">{{ dayName }}</div>
          <div
            v-for="(cell, index) in calendarCells"
            :key="index"
            class="calendar-cell"
            :class="{
              'is-other-month': !cell.inMonth,
              'is-today': cell.isToday,
              'has-events': cell.events.length > 0,
            }">
            <div class="cell-day">{{ cell.day }}</div>
            <div class="cell-events">
              <button
                v-for="eventItem in cell.events"
                :key="eventItem._id"
                type="button"
                class="cell-event"
                :class="eventPhaseClass(getPhase(eventItem))"
                :title="eventItem.title"
                @click="openSession(eventItem)">
                <span class="event-time">{{ formatTime(eventItem.startsAt) }}</span>
                <span class="event-title">{{ eventItem.title }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </section>
</template>

<script>
import api from "@/api"

const DAY_NAMES = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"]

export default {
  name: "Schulungsplan",
  data() {
    return {
      viewMode: "list",
      sessions: [],
      loading: false,
      monthCursor: this.firstOfMonth(new Date()),
      dayNames: DAY_NAMES,
    }
  },
  computed: {
    monthLabel() {
      return this.monthCursor.toLocaleDateString("de-CH", { month: "long", year: "numeric" })
    },
    calendarCells() {
      const year = this.monthCursor.getFullYear()
      const month = this.monthCursor.getMonth()
      const firstDay = new Date(year, month, 1)
      const firstWeekday = (firstDay.getDay() + 6) % 7
      const daysInMonth = new Date(year, month + 1, 0).getDate()
      const cells = []
      const today = new Date()
      const isSameDay = (first, second) =>
        first.getFullYear() === second.getFullYear() &&
        first.getMonth() === second.getMonth() &&
        first.getDate() === second.getDate()

      const previousMonthDays = new Date(year, month, 0).getDate()
      for (let index = firstWeekday - 1; index >= 0; index--) {
        const date = new Date(year, month - 1, previousMonthDays - index)
        cells.push(this.buildCell(date, false, isSameDay(date, today)))
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day)
        cells.push(this.buildCell(date, true, isSameDay(date, today)))
      }

      let nextDay = 1
      while (cells.length % 7 !== 0 || cells.length < 42) {
        const date = new Date(year, month + 1, nextDay++)
        cells.push(this.buildCell(date, false, isSameDay(date, today)))
        if (cells.length >= 42) break
      }
      return cells
    },
  },
  mounted() {
    this.loadSessions()
  },
  methods: {
    firstOfMonth(date) {
      return new Date(date.getFullYear(), date.getMonth(), 1)
    },
    previousMonth() {
      this.monthCursor = new Date(this.monthCursor.getFullYear(), this.monthCursor.getMonth() - 1, 1)
    },
    nextMonth() {
      this.monthCursor = new Date(this.monthCursor.getFullYear(), this.monthCursor.getMonth() + 1, 1)
    },
    goToToday() {
      this.monthCursor = this.firstOfMonth(new Date())
    },
    buildCell(date, inMonth, isToday) {
      const events = this.sessions.filter((session) => {
        if (!session.startsAt) return false
        const start = new Date(session.startsAt)
        return start.getFullYear() === date.getFullYear() && start.getMonth() === date.getMonth() && start.getDate() === date.getDate()
      })
      return { date, day: date.getDate(), inMonth, isToday, events }
    },
    async loadSessions() {
      this.loading = true
      try {
        this.sessions = (await api.akademie.fetchTermine()) || []
      } catch (error) {
        console.error("Fehler beim Laden der Termine:", error)
      } finally {
        this.loading = false
      }
    },
    formatTime(date) {
      if (!date) return ""
      return new Date(date).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
    },
    formatMonthShort(date) {
      if (!date) return ""
      return new Date(date).toLocaleDateString("de-CH", { month: "short" }).replace(".", "").toUpperCase()
    },
    formatDay(date) {
      return date ? new Date(date).getDate() : ""
    },
    getPhase(session) {
      if (!session.registrationStart || !session.registrationEnd) return "Unbekannt"
      const now = new Date()
      const start = new Date(session.registrationStart)
      const end = new Date(session.registrationEnd)
      if (now < start) return "Geplant"
      if (now > end) return "Geschlossen"
      return "Geöffnet"
    },
    phaseClass(phase) {
      if (phase === "Geöffnet") return "phase-open"
      if (phase === "Geplant") return "phase-planned"
      return "phase-closed"
    },
    eventPhaseClass(phase) {
      if (phase === "Geöffnet") return "event-open"
      if (phase === "Geplant") return "event-planned"
      return "event-closed"
    },
    openSession(session) {
      this.$router.push(`/akademie/schulungen/${session._id}`)
    },
  },
}
</script>

<style scoped>
.training-plan {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  min-height: 30rem;
  padding: 0.375rem 0 2rem;
  font-size: 1rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  margin-bottom: 1rem;
  padding: 1.25rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.075), transparent 52%), rgba(255, 255, 255, 0.03);
}

.section-heading {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 0.875rem;
}

.section-heading > div {
  min-width: 0;
}

.section-icon,
.empty-icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.section-icon {
  width: 2.875rem;
  height: 2.875rem;
  border: 1px solid rgba(0, 150, 64, 0.24);
  border-radius: 0.875rem;
}

.section-icon .v-icon,
.empty-icon .v-icon {
  color: currentColor;
}

.section-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.section-heading h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.5rem;
  line-height: 1.25;
}

.section-heading p {
  max-width: 43rem;
  margin: 0.375rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.55;
}

.view-switch {
  display: flex;
  flex: 0 0 auto;
  gap: 0.25rem;
  padding: 0.3125rem;
  border: 1px solid var(--border);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.16);
}

.view-switch button {
  display: inline-flex;
  min-height: 2.75rem;
  align-items: center;
  gap: 0.4375rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid transparent;
  border-radius: 0.6875rem;
  outline: none;
  color: var(--muted);
  background: transparent;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 700;
}

.view-switch button .v-icon {
  color: currentColor;
  font-size: 1.125rem;
}

.view-switch button.active {
  border-color: rgba(0, 150, 64, 0.25);
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.view-switch button:focus-visible,
.cell-event:focus-visible {
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.session-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.875rem;
}

.session-skeleton,
.session-card {
  min-height: 19rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem !important;
  background: var(--surface) !important;
}

.session-card {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  outline: none;
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.session-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.card-top,
.session-footer,
.calendar-toolbar,
.month-controls {
  display: flex;
  align-items: center;
}

.card-top,
.session-footer,
.calendar-toolbar {
  justify-content: space-between;
  gap: 0.75rem;
}

.date-tile {
  display: grid;
  width: 3.375rem;
  min-height: 3.75rem;
  place-content: center;
  border: 1px solid rgba(0, 150, 64, 0.24);
  border-radius: 0.875rem;
  background: rgba(0, 150, 64, 0.11);
  text-align: center;
}

.date-tile strong {
  color: #ffffff;
  font-size: 1.375rem;
  line-height: 1.05;
}

.date-tile small {
  margin-top: 0.1875rem;
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.phase-pill,
.registration-pill {
  display: inline-flex;
  min-height: 1.75rem;
  align-items: center;
  gap: 0.3125rem;
  padding: 0.3125rem 0.625rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 750;
}

.phase-open,
.is-confirmed {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.15);
}

.phase-planned,
.is-waiting {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.15);
}

.phase-closed {
  color: #c7c7cb;
  background: rgba(255, 255, 255, 0.075);
}

.session-copy {
  flex: 1 1 auto;
  margin-top: 1rem;
}

.session-copy h3 {
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  color: var(--text);
  font-size: 1rem;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.session-copy p {
  display: -webkit-box;
  margin: 0.5rem 0 0;
  overflow: hidden;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.session-facts {
  display: grid;
  gap: 0.5rem;
  margin: 1rem 0;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 0.8125rem;
  background: rgba(0, 0, 0, 0.12);
}

.session-facts span,
.seats {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.4375rem;
  color: #c7c7cb;
  font-size: 0.75rem;
}

.session-facts .v-icon,
.seats .v-icon {
  color: #8f9095;
  font-size: 1.125rem;
}

.seats strong {
  color: #ffffff;
  font-size: 0.875rem;
}

.registration-pill .v-icon {
  color: currentColor;
  font-size: 1rem;
}

.card-arrow {
  color: #8f9095 !important;
}

.calendar-surface {
  padding: 1.25rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem !important;
  background: var(--surface) !important;
}

.month-controls {
  gap: 0.375rem;
}

.month-controls h3 {
  min-width: 12rem;
  margin: 0;
  color: var(--text);
  font-size: 1.125rem;
  text-align: center;
  text-transform: capitalize;
}

.icon-button {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.75rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.045) !important;
}

.today-action {
  min-height: 2.75rem !important;
  border-radius: 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.calendar-scroll {
  margin-top: 1rem;
  overflow-x: auto;
  border-radius: 0.875rem;
  outline: none;
  overscroll-behavior-x: contain;
  scrollbar-color: rgba(88, 211, 136, 0.35) transparent;
  scrollbar-width: thin;
}

.calendar-scroll:focus-visible {
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.calendar-grid {
  display: grid;
  min-width: 52rem;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.375rem;
}

.calendar-day-name {
  padding: 0.625rem 0;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-align: center;
  text-transform: uppercase;
}

.calendar-cell {
  display: flex;
  min-height: 6.75rem;
  flex-direction: column;
  padding: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 0.75rem;
  background: rgba(0, 0, 0, 0.14);
}

.calendar-cell.is-other-month {
  opacity: 0.4;
}

.calendar-cell.is-today {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: inset 0 0 0 1px rgba(0, 150, 64, 0.28);
}

.calendar-cell.has-events {
  background: rgba(255, 255, 255, 0.045);
}

.cell-day {
  margin-bottom: 0.375rem;
  color: #e4e4e7;
  font-size: 0.875rem;
  font-weight: 750;
}

.cell-events {
  display: grid;
  gap: 0.25rem;
}

.cell-event {
  display: flex;
  width: 100%;
  min-height: 1.75rem;
  align-items: center;
  gap: 0.3125rem;
  padding: 0.25rem 0.375rem;
  overflow: hidden;
  border: 0;
  border-left: 3px solid transparent;
  border-radius: 0.3125rem;
  outline: none;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.055);
  cursor: pointer;
  font-size: 0.75rem;
  text-align: left;
}

.event-open {
  border-left-color: #58d388;
}

.event-planned {
  border-left-color: #fcd34d;
}

.event-closed {
  border-left-color: #a3a3a8;
}

.event-time {
  flex: 0 0 auto;
  color: #b8b8bc;
  font-variant-numeric: tabular-nums;
}

.event-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-surface {
  display: flex;
  min-height: 20rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 2rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem;
  background: var(--surface);
  text-align: center;
}

.empty-icon {
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.875rem;
  border-radius: 1rem;
}

.empty-surface h3 {
  margin: 0;
  color: var(--text);
  font-size: 1rem;
}

.empty-surface p {
  margin: 0.375rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
}

@media (hover: hover) and (pointer: fine) {
  .session-card:hover {
    transform: translateY(-2px);
    border-color: var(--border-strong);
    background: var(--surface-strong) !important;
  }

  .cell-event:hover {
    background: rgba(0, 150, 64, 0.14);
  }
}

@media (max-width: 78.9375rem) {
  .session-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 46rem) {
  .section-header,
  .calendar-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .view-switch,
  .today-action {
    width: 100%;
  }

  .view-switch button {
    flex: 1 1 50%;
    justify-content: center;
  }

  .month-controls {
    width: 100%;
    justify-content: space-between;
  }

  .month-controls h3 {
    min-width: 0;
  }
}

@media (max-width: 37.4375rem) {
  .training-plan {
    padding-bottom: 1rem;
  }

  .section-header {
    padding: 1rem;
    border-radius: 1.0625rem;
  }

  .section-heading {
    gap: 0.75rem;
  }

  .section-icon {
    width: 2.625rem;
    height: 2.625rem;
  }

  .section-heading h2 {
    font-size: 1.25rem;
  }

  .session-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .session-card {
    min-height: 18rem;
  }

  .calendar-surface {
    padding: 0.875rem;
    border-radius: 1.0625rem !important;
  }
}
</style>
